import fromPairs from 'lodash/fromPairs';
import { v1 as uuid } from 'uuid';
import { misleadSingleton } from '../../app/misleadSingleton';
import {
  CreativeStatus,
  CreativeType,
  INode,
  MessagesInterface,
  Project,
} from '../../../../general/model';
import { CreativeFolderServer } from '../../../../general/modelCreativeFolder';
import splitFilename from '../../../../general/utils/splitFilename';
import {
  Fallback,
  FallbackDictionary,
  fallbackIsPlainDictionary,
  getFallbackForNode,
  getForcedMetaForNode,
} from './fallback';
import { spacesToDashes } from '../../creativeFolder/model';

export const specificFields = {
  // Названия специфичных полей в креатив конвертере
  // Это поле будет заполняться именем креатива на сервере
  SRV_CREATIVE_NODE_NAME: 'node_name',
  // Это поле соответствует проекту
  LIB_PROJECT_NAME: 'project',
  // В этом поле содержится список языков
  LIB_LANGUAGE_DICTIONARY: 'language',
  // В этом поле содержится список платформ
  LIB_PLATFORM_DICTIONARY: 'platform',
};

export enum Statuses {
  ENQUEUED = 'ENQUEUED',
  UPLOAD_INPROGRESS = 'UPLOAD_INPROGRESS',
  UPLOAD_SUCCESS = 'UPLOAD_SUCCESS',
  UPLOAD_FAILURE = 'UPLOAD_FAILURE',

  BIND_INPROGRESS = 'BIND_INPROGRESS',
  BIND_SUCCESS = 'BIND_SUCCESS',
  BIND_FAILURE = 'BIND_FAILURE',

  BL_SUBMIT_INPROGRESS = 'BL_SUBMIT_INPROGRESS',
  BL_SUBMIT_SUCCESS = 'BL_SUBMIT_SUCCESS',
  BL_SUBMIT_FAILURE = 'BL_SUBMIT_FAILURE',

  // файл существует в ФС, необходимо заполнить его мету, переместить в папку креатива и создать бл сущность
  MOVE_IN_PENDING = 'MOVE_IN_PENDING',
}
export const failedStatuses = [
  Statuses.BIND_FAILURE,
  Statuses.UPLOAD_FAILURE,
  Statuses.BL_SUBMIT_FAILURE,
];
export const removableStatuses = [
  ...failedStatuses,
  Statuses.UPLOAD_SUCCESS,
  Statuses.MOVE_IN_PENDING,
];

export const settledStatuses = [
  Statuses.UPLOAD_FAILURE,
  Statuses.BIND_FAILURE,
  Statuses.BL_SUBMIT_FAILURE,
  Statuses.BL_SUBMIT_SUCCESS,
];

export type CreativeUploaderShape = {
  open: boolean;
  submitInprogress: boolean;
  uploadInprogress: boolean;
  submitShown: boolean;
  jobs: Job[];
  dictionaries: Dictionary[];
  blProject: Project | null;
  blType: CreativeType | null;
  blCreative: CreativeFolderServer | null;
  mode: JobMode | null;
  parentNodeId: number | null;
  destinationFilenames: string[] | null;
};

export const initialState: CreativeUploaderShape = {
  uploadInprogress: false,
  submitInprogress: false,
  submitShown: true,
  open: false,
  jobs: [],
  dictionaries: [],
  blProject: null,
  blType: null,
  blCreative: null,
  mode: null,
  parentNodeId: null,
  destinationFilenames: null,
};

export interface FileLike {
  readonly name: string;
}

export enum JobMode {
  UPLOAD = 'UPLOAD',
  UPDATE = 'UPDATE',
  MOVE_IN = 'MOVE_IN',
  COPY_IN = 'COPY_IN',
  CONVERT = 'CONVERT',
}

export type JobMeta = Record<string, string>;

export type Job = {
  file: FileLike;
  status: Statuses;
  storage_id?: string;
  node_id?: number;
  parent_node_id?: number;
  meta: JobMeta;
  uuid: string;
  creative_id?: string;
  relpath: string;
  node?: INode;
};

export enum DictionaryType {
  lib = 'lib',
  str = 'str',
  srv = 'srv',
}

export type Dictionary = {
  name: string;
  header: string;
  optional?: boolean;
} & (
  | { type: DictionaryType.lib; values: string[] }
  | { type: DictionaryType.srv }
  | { type: DictionaryType.str }
);

export type LibDictionary = Extract<Dictionary, { type: DictionaryType.lib }>;

export const dictionaryIsLibDictionary = (d: Dictionary): d is LibDictionary => {
  return d.type === DictionaryType.lib;
};

export const makeJob = (
  file: FileLike,
  parent_node_id?: number,
  storage_id?: string,
  node_id?: number,
  _uuid?: string,
  meta?: Record<string, string>,
): Job => ({
  file,
  status: Statuses.ENQUEUED,
  storage_id,
  node_id,
  parent_node_id,
  uuid: _uuid || uuid(),
  meta: meta || {},
  relpath: '',
});

export type MaskParam = Partial<{
  maxLength: number;
  massFill: boolean;
}>;

export const MASK_PARAMS: Partial<Record<string, MaskParam>> = {
  var: {
    maxLength: 10,
    massFill: true,
  },
  language: {
    massFill: true,
  },
  project: {
    massFill: true,
  },
};

export const Messages: MessagesInterface = {
  loadDictionariesError: () => ({
    title: 'Server request error',
    message: 'Failed to load dictionaries',
  }),
  loadBLInfoError: () => ({
    title: 'Server request error',
    message: 'Complex request failed',
  }),
  allCreativesSubmitted: () => ({
    title: 'All creatives have been created successfully',
  }),
  allCreativesUpdated: () => ({
    title: 'All creatives have been updated successfully',
  }),
  allCreativesImported: () => ({
    title: 'All creatives have been imported successfully',
  }),
  creativesFailed: (failedJobFilenames) => ({
    title: 'Server request error',
    message: `Failed to submit creatives ${failedJobFilenames}`,
  }),
  creativesFailedImport: (failedJobFilenames) => ({
    title: 'Server request error',
    message: `Failed to import creatives ${failedJobFilenames}`,
  }),
  reusingCreative: () => ({
    title: 'Creative folder exists',
    message: 'We will import files there',
  }),
  cantImportFolders: () => ({
    title: 'Can not import folders',
  }),
};

export function tryParse(
  filename: string,
  settings: CreativeBLSettings,
  // словарь значений, которые надо подставить, если автоматический парсинг не удался
  fallback: FallbackDictionary,
): Record<string, string> {
  const result: Record<string, string> = {};

  let restName = splitFilename(filename).baseName;
  restName = spacesToDashes(restName);

  let parsingError = false;
  const SEPARATOR = '_';

  const cutNextPart = () => {
    if (parsingError) {
      return '';
    }
    const part = restName.split(SEPARATOR)[0];
    restName = restName.slice(part.length + 1);
    return part;
  };

  settings.dictionaries.forEach((dict) => {
    if (dict.type === DictionaryType.srv) {
      if (dict.name === specificFields.SRV_CREATIVE_NODE_NAME) {
        const startsWithNodeName =
          restName.toLowerCase().indexOf(settings.creative.node_name.toLowerCase()) === 0;
        const nextChar = restName[settings.creative.node_name.length];
        if (startsWithNodeName && (nextChar === SEPARATOR || !nextChar)) {
          restName = restName.slice(settings.creative.node_name.length + nextChar.length);
        } else {
          parsingError = true;
        }
        result[dict.name] = settings.creative.node_name;
      } else {
        cutNextPart();
        result[dict.name] = '';
      }
    } else if (dict.type === DictionaryType.lib) {
      if (dict.name === specificFields.LIB_PROJECT_NAME) {
        cutNextPart();
        result[dict.name] = settings.project.short_name;
      } else {
        const part = cutNextPart();
        result[dict.name] =
          (dict.values ?? []).find((val) => part.toLowerCase() === val.toLowerCase()) ?? '';
      }
    } else if (dict.type === DictionaryType.str) {
      const part = cutNextPart();
      const maxLength = MASK_PARAMS[dict.name]?.maxLength;
      result[dict.name] = maxLength ? part.substring(0, maxLength) : part;
    }
  });

  Object.keys(result).forEach((name) => {
    if (!result[name] && fallback[name] !== undefined) {
      result[name] = fallback[name];
    }
  });

  return result;
}

/**
 *
 * @param mode режим работы креативаплоадера. Если `UPDATE`, то вся мета уже есть в джобе и ее не надо парсить
 * @param dictionaries словари для парсинга
 * @param jobs джобы
 * @returns {jobId, meta}[]
 */
export const fillMeta = (
  mode: JobMode,
  settings: CreativeBLSettings,
  jobs: Job[],
  fallback: Fallback = {},
): { jobId: string; meta: JobMeta }[] => {
  const dictPairs = settings.dictionaries.map((maskItem) => [maskItem.name, '']);
  const blankMeta: Record<string, string> = fromPairs(dictPairs);
  // try to parse only new files

  const resultMetas = jobs.map((j) => {
    let nodeFallback: FallbackDictionary = {};
    let forcedMeta: FallbackDictionary = {};
    if (fallbackIsPlainDictionary(fallback)) {
      nodeFallback = fallback;
    } else if (j.node_id) {
      nodeFallback = getFallbackForNode(fallback, j.node_id);
      forcedMeta = getForcedMetaForNode(fallback, j.node_id);
    }

    const meta: Record<string, string> =
      mode === JobMode.UPDATE
        ? { ...tryParse(j.file.name, settings, nodeFallback), ...j.meta }
        : { ...tryParse(j.file.name, settings, nodeFallback), ...forcedMeta };

    return { jobId: j.uuid, meta: { ...blankMeta, ...meta } };
  });
  return resultMetas;
};

export type CreativeBLSettings = {
  dictionaries: Dictionary[];
  creative: CreativeFolderServer;
  project: Project;
  type: CreativeType;
};

export const makeCreative = (draft: Partial<CreativeFolderServer>): CreativeFolderServer => {
  return {
    benchmark_install_adset_id: '',
    benchmark_spend_adset_id: '',
    benchmark_spend_creative_name: '',
    customer_id: NaN,
    date_rating: '',
    date_start_testing: '',
    description: '',
    executor_id: NaN,
    id: NaN,
    install_adset_id: '',
    install_adset_settings: '',
    node_id: 0,
    node_name: '',
    project_id: 0,
    rating: null,
    spend_adset_id: '',
    spend_adset_settings: '',
    status: CreativeStatus.new,
    testing_status: '',
    is_benchmark: false,
    test_ir: NaN,
    type_id: 0,
    mislead_level: misleadSingleton.getUnset()?.id ?? null,
    customer: null,
    executor: null,
    benchmark_test_stage_1_result: null,
    benchmark_test_stage_1_icon: null,
    benchmark_test_stage_2_result: null,
    benchmark_test_stage_2_icon: null,
    team: null,
    av_playtime: null,
    ir_av_playtime: null,
    benchmark: null,
    genre: null,
    genres: null,
    intro: null,
    duration: null,
    tags: null,
    source_creatives: [],
    has_creative_changes: false,
    plot_recognition: null,
    ...draft,
  };
};

export type CreativeUploaderRow = Job & {
  newFilename: string;
  id: string;
};
